import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { 
    Card, 
    Spinner, 
    Badge, 
    Alert, 
    Button, 
    Table, 
    Tabs, 
    Tab,
    Row,
    Col
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { API_URL } from "../../utils/constant";
import { 
    faFileAlt,
    faArrowLeft,
    faTag,
    faClock,
    faUser,
    faNetworkWired,
    faInfoCircle,
    faProjectDiagram
} from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import CampaignGraph from './CampaignGraph';

const CampaignDetails = () => {
    const { id } = useParams();
    const history = useHistory();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [campaign, setCampaign] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');

    // Fetch campaign details
    const fetchCampaignDetails = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const response = await fetch(`${API_URL}/threat-intel/campaigns/${id}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('session')}`,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();

            if (data.error) {
                throw new Error(data.userMessage || 'Failed to fetch campaign details');
            }

            setCampaign(data.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchCampaignDetails();
        }
    }, [id]);

    // Filter out relationship objects and extract actual entities
    const relatedObjects = React.useMemo(() => {
        if (!campaign?.objects?.edges) return [];
        
        const relationshipTypes = ['indicates', 'uses', 'targets', 'based-on', 'related-to'];
        const entityMap = new Map();
        
        // First pass: collect all actual entities
        campaign.objects.edges.forEach((edge) => {
            const node = edge.node;
            if (node && !relationshipTypes.includes(node.entity_type?.toLowerCase())) {
                // Only add if not already in map (avoid duplicates)
                if (!entityMap.has(node.id)) {
                    entityMap.set(node.id, node);
                }
            }
        });
        
        return Array.from(entityMap.values());
    }, [campaign]);

    const getStatusColor = (status) => {
        if (!status?.template) return 'secondary';
        const statusName = status.template.name?.toLowerCase() || '';
        if (statusName.includes('progress') || statusName.includes('active')) return 'info';
        if (statusName.includes('complete') || statusName.includes('done')) return 'success';
        if (statusName.includes('draft')) return 'warning';
        return 'secondary';
    };

    if (loading) {
        return (
            <div className="container-fluid">
                <div className="content-wrapper">
                    <div className="text-center my-5 py-5">
                        <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
                        <p className="mt-3 text-muted">Loading campaign details...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container-fluid">
                <div className="content-wrapper">
                    <Alert variant="danger" className="mb-4">
                        <Alert.Heading>Error</Alert.Heading>
                        <p>{error}</p>
                        <Button variant="outline-danger" onClick={() => history.goBack()}>
                            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                            Go Back
                        </Button>
                    </Alert>
                </div>
            </div>
        );
    }

    if (!campaign) {
        return (
            <div className="container-fluid">
                <div className="content-wrapper">
                    <Alert variant="warning" className="mb-4">
                        <Alert.Heading>Not Found</Alert.Heading>
                        <p>Campaign not found.</p>
                        <Button variant="outline-warning" onClick={() => history.goBack()}>
                            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                            Go Back
                        </Button>
                    </Alert>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid">
            <div className="content-wrapper">
                {/* Header */}
                <div className="d-flex align-items-center justify-content-between mb-4">
                    <div>
                        <Button 
                            variant="outline-secondary" 
                            onClick={() => history.goBack()}
                            className="mb-3"
                        >
                            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                            Back to Campaigns
                        </Button>
                        <div className="block-header">
                            <h2 className="welcome-title">{campaign.name}</h2>
                            <p className="welcome-textSmall">Campaign Details & Intelligence</p>
                        </div>
                    </div>
                    {campaign.status && (
                        <div className="text-center">
                            <Badge bg={getStatusColor(campaign.status)} className="px-3 py-2" style={{ fontSize: '1rem' }}>
                                {campaign.status.template?.name || 'Unknown Status'}
                            </Badge>
                        </div>
                    )}
                </div>

                {/* Tabs */}
                <Card className="shadow-sm" style={{ border: '1px solid #e9ecef', borderRadius: '12px' }}>
                    <style>{`
                        .campaign-tabs .nav-tabs {
                            border-bottom: none !important;
                        }
                        .campaign-tabs .nav-tabs .nav-link {
                            color: rgba(255, 255, 255, 0.7) !important;
                            background-color: transparent !important;
                            border: none !important;
                            border-bottom: 2px solid transparent !important;
                            padding: 12px 20px !important;
                        }
                        .campaign-tabs .nav-tabs .nav-link:hover {
                            color: white !important;
                            background-color: rgba(255, 255, 255, 0.1) !important;
                            border-bottom: 2px solid rgba(255, 255, 255, 0.3) !important;
                        }
                        .campaign-tabs .nav-tabs .nav-link.active {
                            color: white !important;
                            background-color: rgba(255, 255, 255, 0.15) !important;
                            border-bottom: 2px solid white !important;
                        }
                    `}</style>
                    <Card.Header style={{ 
                        background: 'linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)', 
                        color: 'white',
                        padding: '0',
                        borderBottom: 'none'
                    }}>
                        <Tabs
                            activeKey={activeTab}
                            onSelect={(k) => setActiveTab(k)}
                            className="border-0 campaign-tabs"
                            style={{ borderBottom: 'none' }}
                        >
                            <Tab 
                                eventKey="overview" 
                                title={
                                    <span>
                                        <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                                        Overview
                                    </span>
                                }
                            />
                            <Tab 
                                eventKey="objects" 
                                title={
                                    <span>
                                        <FontAwesomeIcon icon={faNetworkWired} className="me-2" />
                                        Related Objects ({relatedObjects.length})
                                    </span>
                                }
                            />
                            <Tab 
                                eventKey="graph" 
                                title={
                                    <span>
                                        <FontAwesomeIcon icon={faProjectDiagram} className="me-2" />
                                        Graph View
                                    </span>
                                }
                            />
                        </Tabs>
                    </Card.Header>
                    <Card.Body className="p-4">
                        {/* Overview Tab */}
                        {activeTab === 'overview' && (
                            <div>
                                {/* Basic Information */}
                                <Row className="mb-4">
                                    <Col md={8}>
                                        <Card className="mb-4" style={{ border: '1px solid #e9ecef', borderRadius: '12px' }}>
                                            <Card.Header style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                                                <h5 className="mb-0 fw-bold">
                                                    <FontAwesomeIcon icon={faInfoCircle} className="me-2 text-primary" />
                                                    Basic Information
                                                </h5>
                                            </Card.Header>
                                            <Card.Body>
                                                {campaign.description && (
                                                    <div className="mb-3">
                                                        <strong className="text-muted small">Description:</strong>
                                                        <p className="mt-1 mb-0" style={{ lineHeight: '1.6' }}>
                                                            {campaign.description.replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1')}
                                                        </p>
                                                    </div>
                                                )}
                                                <Row>
                                                    <Col md={6}>
                                                        <div className="mb-3">
                                                            <strong className="text-muted small">Standard ID:</strong>
                                                            <div className="mt-1">
                                                                <code className="small">{campaign.standard_id || 'N/A'}</code>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                    <Col md={6}>
                                                        <div className="mb-3">
                                                            <strong className="text-muted small">Entity Type:</strong>
                                                            <div className="mt-1">{campaign.entity_type || 'N/A'}</div>
                                                        </div>
                                                    </Col>
                                                </Row>
                                                {campaign.report_types && campaign.report_types.length > 0 && (
                                                    <div className="mb-3">
                                                        <strong className="text-muted small">Report Types:</strong>
                                                        <div className="mt-1">
                                                            {campaign.report_types.map((type, idx) => (
                                                                <Badge key={idx} bg="info" className="me-2">
                                                                    {type}
                                                                </Badge>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                {campaign.confidence !== undefined && (
                                                    <div className="mb-3">
                                                        <strong className="text-muted small">Confidence:</strong>
                                                        <div className="mt-1">
                                                            <Badge bg={campaign.confidence >= 80 ? 'danger' : campaign.confidence >= 50 ? 'warning' : 'secondary'}>
                                                                {campaign.confidence}%
                                                            </Badge>
                                                        </div>
                                                    </div>
                                                )}
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                    <Col md={4}>
                                        <Card className="mb-4" style={{ border: '1px solid #e9ecef', borderRadius: '12px' }}>
                                            <Card.Header style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                                                <h5 className="mb-0 fw-bold">
                                                    <FontAwesomeIcon icon={faTag} className="me-2 text-primary" />
                                                    Attributes
                                                </h5>
                                            </Card.Header>
                                            <Card.Body>
                                                {campaign.published && (
                                                    <div className="mb-3">
                                                        <strong className="text-muted small">Published:</strong>
                                                        <div className="mt-1">
                                                            <FontAwesomeIcon icon={faClock} className="me-2 text-muted" />
                                                            {moment(campaign.published).format('MMM DD, YYYY HH:mm')}
                                                        </div>
                                                    </div>
                                                )}
                                                {campaign.created && (
                                                    <div className="mb-3">
                                                        <strong className="text-muted small">Created:</strong>
                                                        <div className="mt-1">
                                                            <FontAwesomeIcon icon={faClock} className="me-2 text-muted" />
                                                            {moment(campaign.created).format('MMM DD, YYYY HH:mm')}
                                                        </div>
                                                    </div>
                                                )}
                                                {campaign.modified && (
                                                    <div className="mb-3">
                                                        <strong className="text-muted small">Modified:</strong>
                                                        <div className="mt-1">
                                                            <FontAwesomeIcon icon={faClock} className="me-2 text-muted" />
                                                            {moment(campaign.modified).format('MMM DD, YYYY HH:mm')}
                                                        </div>
                                                    </div>
                                                )}
                                                {campaign.createdBy && (
                                                    <div className="mb-3">
                                                        <strong className="text-muted small">Created By:</strong>
                                                        <div className="mt-1">
                                                            <FontAwesomeIcon icon={faUser} className="me-2 text-muted" />
                                                            {campaign.createdBy.name || 'Unknown'}
                                                        </div>
                                                    </div>
                                                )}
                                                {campaign.creators && campaign.creators.length > 0 && (
                                                    <div className="mb-3">
                                                        <strong className="text-muted small">Creators:</strong>
                                                        <div className="mt-1">
                                                            {campaign.creators.map((creator, idx) => (
                                                                <Badge key={idx} bg="secondary" className="me-1 mb-1">
                                                                    {creator.name}
                                                                </Badge>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                </Row>

                                {/* Labels */}
                                {campaign.objectLabel && campaign.objectLabel.length > 0 && (
                                    <Card className="mb-4" style={{ border: '1px solid #e9ecef', borderRadius: '12px' }}>
                                        <Card.Header style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                                            <h5 className="mb-0 fw-bold">
                                                <FontAwesomeIcon icon={faTag} className="me-2 text-primary" />
                                                Labels
                                            </h5>
                                        </Card.Header>
                                        <Card.Body>
                                            <div className="d-flex flex-wrap gap-2">
                                                {campaign.objectLabel.map((label, idx) => (
                                                    <Badge 
                                                        key={idx}
                                                        style={{ 
                                                            backgroundColor: label.color || '#6c757d',
                                                            color: '#FFFFFF',
                                                            fontSize: '0.85rem',
                                                            padding: '6px 12px',
                                                            borderRadius: '6px'
                                                        }}
                                                    >
                                                        {label.value}
                                                    </Badge>
                                                ))}
                                            </div>
                                        </Card.Body>
                                    </Card>
                                )}

                                {/* Markings */}
                                {campaign.objectMarking && campaign.objectMarking.length > 0 && (
                                    <Card className="mb-4" style={{ border: '1px solid #e9ecef', borderRadius: '12px' }}>
                                        <Card.Header style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                                            <h5 className="mb-0 fw-bold">
                                                <FontAwesomeIcon icon={faTag} className="me-2 text-primary" />
                                                Markings
                                            </h5>
                                        </Card.Header>
                                        <Card.Body>
                                            <div className="d-flex flex-wrap gap-2">
                                                {campaign.objectMarking.map((marking, idx) => (
                                                    <Badge 
                                                        key={idx}
                                                        bg="warning"
                                                        text="dark"
                                                        style={{ 
                                                            fontSize: '0.85rem',
                                                            padding: '6px 12px',
                                                            borderRadius: '6px'
                                                        }}
                                                    >
                                                        {marking.definition || marking.definition_type || 'N/A'}
                                                    </Badge>
                                                ))}
                                            </div>
                                        </Card.Body>
                                    </Card>
                                )}
                            </div>
                        )}

                        {/* Related Objects Tab */}
                        {activeTab === 'objects' && (
                            <div>
                                {relatedObjects.length === 0 ? (
                                    <div className="text-center py-5">
                                        <FontAwesomeIcon icon={faInfoCircle} className="text-muted mb-3" style={{ fontSize: '4rem', opacity: 0.5 }} />
                                        <h5 className="text-muted mb-2">No related objects found</h5>
                                        <p className="text-muted">No related entities associated with this campaign.</p>
                                    </div>
                                ) : (
                                    <div style={{ overflowX: 'auto', width: '100%' }}>
                                        <Table striped bordered hover responsive style={{ tableLayout: 'fixed', width: '100%' }}>
                                            <colgroup>
                                                <col style={{ width: '10%' }} />
                                                <col style={{ width: '25%' }} />
                                                <col style={{ width: '30%' }} />
                                                <col style={{ width: '20%' }} />
                                                <col style={{ width: '15%' }} />
                                            </colgroup>
                                            <thead className="table-light">
                                                <tr>
                                                    <th style={{ whiteSpace: 'nowrap', padding: '12px 8px' }}>Type</th>
                                                    <th style={{ padding: '12px 8px' }}>Name / Identifier</th>
                                                    <th style={{ padding: '12px 8px' }}>Additional Info</th>
                                                    <th style={{ padding: '12px 8px' }}>Labels</th>
                                                    <th style={{ padding: '12px 8px' }}>Markings</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                            {relatedObjects.map((obj, idx) => {
                                                let nameOrIdentifier = obj.name || obj.id;
                                                let additionalInfo = '-';
                                                let typeBadgeColor = 'info';

                                                switch (obj.entity_type) {
                                                    case 'Malware':
                                                        typeBadgeColor = 'danger';
                                                        additionalInfo = obj.malware_types?.join(', ') || '-';
                                                        break;
                                                    case 'Indicator':
                                                        typeBadgeColor = 'warning';
                                                        nameOrIdentifier = obj.name || obj.pattern || obj.id;
                                                        additionalInfo = (
                                                            <>
                                                                Pattern: <code className="small">{obj.pattern || '-'}</code><br/>
                                                                {obj.valid_from && obj.valid_until && (
                                                                    <>Valid: {moment(obj.valid_from).format('MMM DD, YYYY')} - {moment(obj.valid_until).format('MMM DD, YYYY')}</>
                                                                )}
                                                            </>
                                                        );
                                                        break;
                                                    case 'Intrusion-Set':
                                                        typeBadgeColor = 'dark';
                                                        break;
                                                    case 'Attack-Pattern':
                                                        typeBadgeColor = '';
                                                        nameOrIdentifier = obj.name || obj.x_mitre_id || obj.id;
                                                        additionalInfo = (
                                                            <>
                                                                MITRE ID: <Badge bg="secondary" style={{ color: 'white', backgroundColor: '#6c757d' }}>{obj.x_mitre_id || '-'}</Badge><br/>
                                                                {obj.killChainPhases && obj.killChainPhases.length > 0 && (
                                                                    <>Phases: {obj.killChainPhases.map(p => p.phase_name).join(', ')}</>
                                                                )}
                                                            </>
                                                        );
                                                        break;
                                                    case 'StixFile':
                                                        typeBadgeColor = 'success';
                                                        nameOrIdentifier = obj.name || (obj.hashes && obj.hashes[0] ? `${obj.hashes[0].algorithm}: ${obj.hashes[0].hash.substring(0, 16)}...` : obj.id);
                                                        additionalInfo = (
                                                            <>
                                                                {obj.hashes && obj.hashes.length > 0 && (
                                                                    <>
                                                                        Hash ({obj.hashes[0].algorithm}): <code className="small">{obj.hashes[0].hash || '-'}</code><br/>
                                                                    </>
                                                                )}
                                                                {obj.size && <>Size: {obj.size} bytes</>}
                                                            </>
                                                        );
                                                        break;
                                                    case 'Vulnerability':
                                                        typeBadgeColor = 'warning';
                                                        nameOrIdentifier = obj.name || obj.id;
                                                        additionalInfo = (
                                                            <>
                                                                {obj.x_opencti_cve && (
                                                                    <>CVE: <Badge bg="danger">{obj.x_opencti_cve}</Badge></>
                                                                )}
                                                            </>
                                                        );
                                                        break;
                                                    default:
                                                        typeBadgeColor = 'info';
                                                        break;
                                                }

                                                return (
                                                    <tr key={idx}>
                                                        <td style={{ padding: '12px 8px', verticalAlign: 'top' }}>
                                                            <Badge bg={typeBadgeColor}>
                                                                {obj.entity_type || 'Unknown'}
                                                            </Badge>
                                                        </td>
                                                        <td className="fw-semibold" style={{ 
                                                            wordBreak: 'break-word', 
                                                            wordWrap: 'break-word', 
                                                            whiteSpace: 'normal',
                                                            overflowWrap: 'break-word',
                                                            padding: '12px 8px', 
                                                            verticalAlign: 'top',
                                                            lineHeight: '1.5'
                                                        }}>{nameOrIdentifier}</td>
                                                        <td style={{ 
                                                            wordBreak: 'break-word', 
                                                            wordWrap: 'break-word', 
                                                            whiteSpace: 'normal',
                                                            overflowWrap: 'break-word',
                                                            fontSize: '0.85rem', 
                                                            padding: '12px 8px', 
                                                            verticalAlign: 'top',
                                                            lineHeight: '1.5'
                                                        }}>{additionalInfo}</td>
                                                        <td style={{ wordBreak: 'break-word', wordWrap: 'break-word', padding: '12px 8px', verticalAlign: 'top' }}>
                                                            {obj.objectLabel && obj.objectLabel.length > 0 ? (
                                                                <div className="d-flex flex-wrap gap-1">
                                                                    {obj.objectLabel.slice(0, 3).map((label, labelIdx) => (
                                                                        <Badge
                                                                            key={labelIdx}
                                                                            style={{
                                                                                backgroundColor: label.color || '#6c757d',
                                                                                color: '#FFFFFF',
                                                                                fontSize: '0.75rem'
                                                                            }}
                                                                        >
                                                                            {label.value}
                                                                        </Badge>
                                                                    ))}
                                                                    {obj.objectLabel.length > 3 && (
                                                                        <Badge bg="secondary" style={{ fontSize: '0.75rem' }}>
                                                                            +{obj.objectLabel.length - 3}
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                            ) : '-'}
                                                        </td>
                                                        <td style={{ padding: '12px 8px', verticalAlign: 'top' }}>
                                                            {obj.objectMarking && obj.objectMarking.length > 0 ? (
                                                                <div className="d-flex flex-wrap gap-1">
                                                                    {obj.objectMarking.slice(0, 2).map((marking, markingIdx) => (
                                                                        <Badge
                                                                            key={markingIdx}
                                                                            bg="light"
                                                                            text="dark"
                                                                            style={{ fontSize: '0.75rem' }}
                                                                        >
                                                                            {marking.definition || marking.definition_type || '-'}
                                                                        </Badge>
                                                                    ))}
                                                                    {obj.objectMarking.length > 2 && (
                                                                        <Badge bg="secondary" style={{ fontSize: '0.75rem' }}>
                                                                            +{obj.objectMarking.length - 2}
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                            ) : '-'}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Graph Tab */}
                        {activeTab === 'graph' && (
                            <div>
                                <CampaignGraph 
                                    campaignId={id} 
                                    container={campaign ? { id: campaign.id, name: campaign.name } : null}
                                />
                            </div>
                        )}
                    </Card.Body>
                </Card>
            </div>
        </div>
    );
};

export default CampaignDetails;
